<template>
  <PreviewModal
    v-if="isMobile"
    :status="props.status"
    :body="props.body"
    :contexts="props.contexts"
    @close="$emit('update:showingPreview', false)"
  />
  <template v-else>
    <div class="@container relative flex grow flex-col" v-bind="$attrs">
      <div
        class="z-10 h-[53px] shrink-0 items-center justify-between gap-2 border-b border-gray-300 w-full"
        :class="[isMounted && 'transition-all']"
      >
        <View.Header
          :breadcrumbs="[
            {
              state: 'route',
              text: 'Preview',
            },
          ]"
          :hide-sidebar="!isMobile"
        >
          <template #actions>
            <div class="lg:hidden">
              <List.Action
                icon="arrow-left"
                text="Back"
                variant="secondary"
                @click="$emit('update:showingPreview', false)"
              />
            </div>

            <div class="grow"></div>

            <List.Action
              v-if="props.draftTo"
              icon="eye"
              text="Send draft"
              variant="secondary"
              :route="props.draftTo" />

            <Menu.Dropdown v-if="relevantContexts.length > 1" label="Context">
              <Menu.Item
                v-for="context in relevantContexts"
                :key="context"
                :icon="CONTEXT_TO_CONFIGURATION[context].icon"
                :label="CONTEXT_TO_CONFIGURATION[context].title"
                :active="mode === context"
                @click="mode = context"
              />
            </Menu.Dropdown>

            <button
              type="button"
              aria-label="Expand preview"
              class="flex select-none items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 bg-gray-100 p-1.5 transition-colors hover:bg-gray-200"
              @click="openExpandedDialog = true"
            >
              <ArrowsPointingOutIcon
                class="h-4 w-4 text-gray-700"
              /></button></template
        ></View.Header>
      </div>

      <EmailPreview
        :mode="mode"
        :body="props.body"
        :status="props.status"
        :class="{
          'transition-all': isMounted,
        }"
      />
    </div>

    <PreviewModal
      v-if="openExpandedDialog"
      :status="props.status"
      :body="props.body"
      :contexts="props.contexts"
      @close="openExpandedDialog = false"
    />
  </template>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";

import Menu from "@/design_system/Menu";
import View from "@/design_system/View";
import List from "@/design_system/View/List";
import ArrowsPointingOutIcon from "@/icons/heroicons/arrows-pointing-out-micro.svg";
import { useMediaQuery } from "@/lib/media_query";
import { RouteLocation } from "@/router/types";
import { useStore } from "@/store/stores/scaffolding";
import { Context } from "@/types/preview";
import { RenderedMarkdown } from "@/utils";

import EmailPreview from "./previews/EmailPreview.vue";
import PreviewModal from "./previews/PreviewModal.vue";
import { CONTEXT_TO_CONFIGURATION } from "./previews/types";

// Immediately setting `transition-all` seems to result in transitioning on
// mount, we only want transitions to happen on changing breakpoints.
const isMounted = ref(false);

const isMobile = useMediaQuery("(pointer: coarse) and (width < 768px)");
const openExpandedDialog = ref(false);

const store = useStore();

const newsletter = computed(() => store.newsletter);

const props = withDefaults(
  defineProps<{
    status: "success" | "pending";
    body: RenderedMarkdown;
    contexts?: Context[];
    draftTo?: RouteLocation;
  }>(),
  {
    contexts: () => ["email", "web", "email_free", "web_free"],
  }
);

const mode = ref<Context>(props.contexts[0]);

defineEmits(["update:showingPreview"]);

onMounted(() => {
  // Seems like we need to wait one more tick for everything to settle in.
  requestAnimationFrame(() => {
    isMounted.value = true;
  });
});

const relevantContexts = computed(() => {
  return props.contexts.filter((context) =>
    context === "web"
      ? newsletter.value?.enabled_features.includes("archives")
      : context === "email_free"
      ? newsletter.value?.paid_subscriptions_status === "active"
      : context === "web_free"
      ? newsletter.value?.paid_subscriptions_status === "active" &&
        newsletter.value?.enabled_features.includes("archives")
      : true
  );
});
</script>
